import * as React from "react"
import { Dialog as SheetPrimitive } from "radix-ui"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Sheet = SheetPrimitive.Root

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "vonk-dialog-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  )
}

/** `nav` is the original drawer; `md`/`lg` are light form panels (spec-63, DS-15). */
const SHEET_SIZES = {
  nav: "w-[17rem] max-w-[85vw]",
  md: "w-full max-w-full overflow-y-auto bg-card p-6 text-card-foreground sm:w-[28rem]",
  lg: "w-full max-w-full overflow-y-auto bg-card p-6 text-card-foreground sm:w-[40rem]",
} as const

function SheetContent({
  className,
  overlayClassName,
  children,
  side = "left",
  size = "nav",
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "left" | "right"
  size?: keyof typeof SHEET_SIZES
  /** Extra classes for the scrim. Needed to breakpoint-scope the whole sheet:
   *  hiding only the panel would leave the overlay covering the page. */
  overlayClassName?: string
}) {
  return (
    <SheetPrimitive.Portal>
      <SheetOverlay className={overlayClassName} />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        data-size={size}
        className={cn(
          "fixed inset-y-0 z-50 flex h-svh flex-col gap-2 p-4 shadow-xl transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-200 data-[state=open]:duration-300",
          SHEET_SIZES[size],
          side === "left"
            ? "left-0 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left"
            : "right-0 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
          className,
        )}
        {...props}
      >
        {/* Before `children`, so dismissing is the drawer's first tab stop
            instead of the one after every nav item. Out of flow (`absolute`),
            so DOM order costs nothing visually. */}
        <SheetPrimitive.Close
          aria-label="Sluiten"
          className="absolute top-4 right-4 rounded-md p-2 opacity-80 transition-opacity hover:opacity-100"
        >
          <X className="size-[18px]" />
        </SheetPrimitive.Close>
        {children}
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-sm font-semibold", className)}
      {...props}
    />
  )
}

export { Sheet, SheetContent, SheetTitle }
